import React from "react";
import { useLocation } from "react-router";

import { differenceInMilliseconds, isBefore } from "date-fns";
import { noop } from "lodash";

export type OngoingMeeting = {
	guid: string;
	title: string;
	url: string;
	end: string;
};

const OngoingMeetingContext = React.createContext<{
	ongoingMeeting: OngoingMeeting | null;
	setOngoingMeeting: (meeting: OngoingMeeting) => void;
	dismissPrompt: () => void;
}>({
	ongoingMeeting: null,
	setOngoingMeeting: noop,
	dismissPrompt: noop,
});
OngoingMeetingContext.displayName = "OngoingMeetingContext";

export default OngoingMeetingContext;

export function OngoingMeetingContextProvider({ children }: React.PropsWithChildren<{}>) {
	const location = useLocation();
	const [ongoingMeeting, setOngoingMeeting] = React.useState<OngoingMeeting | null>(null);
	const [meetingToShow, setMeetingToShow] = React.useState<OngoingMeeting | null>(null);

	React.useEffect(() => {
		if (!ongoingMeeting) return;

		setMeetingToShow(
			!location.pathname.includes(ongoingMeeting.guid) && isBefore(new Date(), new Date(ongoingMeeting.end))
				? ongoingMeeting
				: null,
		);
	}, [location, ongoingMeeting]);

	const dismissPrompt = React.useCallback(() => {
		setOngoingMeeting(null);
		setMeetingToShow(null);
	}, []);

	React.useEffect(() => {
		if (!meetingToShow) return;

		const millisecondsRemaining = differenceInMilliseconds(new Date(meetingToShow.end), new Date());
		if (millisecondsRemaining <= 0) return; // this should never be the case

		const timeout = window.setTimeout(dismissPrompt, millisecondsRemaining);

		return () => clearTimeout(timeout);
	}, [meetingToShow, dismissPrompt]);

	return (
		<OngoingMeetingContext.Provider
			value={React.useMemo(
				() => ({ ongoingMeeting: meetingToShow, dismissPrompt, setOngoingMeeting }),
				[meetingToShow, dismissPrompt],
			)}
		>
			{children}
		</OngoingMeetingContext.Provider>
	);
}
